import React, { useState } from "react";
import {
  Box,
  IconButton,
  Popover,
  Stack,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import SignalWifi4BarIcon from "@mui/icons-material/SignalWifi4Bar";
import SignalWifi3BarIcon from "@mui/icons-material/NetworkWifi3Bar";
import SignalWifi1BarIcon from "@mui/icons-material/NetworkWifi1Bar";
import SignalWifiOffIcon from "@mui/icons-material/SignalWifiOff";
import { useSelector } from "react-redux";

// ----------------------------------------------------------------------
// Clasificación de calidad a partir de la latencia media + online/offline.
// Los umbrales están pensados para un POS en LAN wifi: <300ms = normal,
// 300-1000ms = noticeable, >1000ms = degrada claramente la experiencia.
// ----------------------------------------------------------------------

function classify({ isOnline, avgLatencyMs, consecutiveFailures, effectiveType }) {
  if (!isOnline || consecutiveFailures >= 3) {
    return { level: "offline", color: "error.main", label: "Sin conexión" };
  }
  if (effectiveType === "slow-2g" || effectiveType === "2g") {
    return { level: "critical", color: "error.main", label: "Conexión muy lenta" };
  }
  if (avgLatencyMs == null) {
    return { level: "unknown", color: "text.secondary", label: "Midiendo..." };
  }
  if (avgLatencyMs > 2000 || effectiveType === "3g") {
    return { level: "slow", color: "warning.main", label: "Conexión lenta" };
  }
  if (avgLatencyMs > 800) {
    return { level: "fair", color: "warning.main", label: "Conexión irregular" };
  }
  return { level: "good", color: "success.main", label: "Conexión estable" };
}

function pickIcon(level) {
  switch (level) {
    case "offline":
    case "critical":
      return SignalWifiOffIcon;
    case "slow":
      return SignalWifi1BarIcon;
    case "fair":
      return SignalWifi3BarIcon;
    case "good":
    default:
      return SignalWifi4BarIcon;
  }
}

function fmtMs(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

export default function NetworkIndicator() {
  const {
    isOnline,
    avgLatencyMs,
    effectiveType,
    downlinkMbps,
    rttMs,
    consecutiveFailures,
    lastSampleAt,
    latencySamples,
  } = useSelector((s) => s.networkStatus);

  const quality = classify({ isOnline, avgLatencyMs, consecutiveFailures, effectiveType });
  const Icon = pickIcon(quality.level);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={quality.label}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ color: quality.color }}
        >
          <Icon />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
            <Icon sx={{ color: quality.color }} />
            <Box>
              <Typography variant="subtitle2" color={quality.color}>
                {quality.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isOnline ? "Conectado a Internet" : "Sin acceso a Internet"}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 1.5 }} />

          <Stack spacing={0.75}>
            <Row label="Latencia media (API)" value={fmtMs(avgLatencyMs)} />
            <Row
              label="Muestras"
              value={latencySamples?.length ? `${latencySamples.length} últimas` : "—"}
            />
            {rttMs != null && <Row label="RTT navegador" value={fmtMs(rttMs)} />}
            {effectiveType && (
              <Row label="Tipo de conexión" value={effectiveType.toUpperCase()} />
            )}
            {downlinkMbps != null && (
              <Row label="Ancho de banda" value={`~${downlinkMbps} Mbps`} />
            )}
            {consecutiveFailures > 0 && (
              <Row
                label="Fallos consecutivos"
                value={`${consecutiveFailures}`}
                color="error.main"
              />
            )}
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="caption" color="text.secondary">
            {quality.level === "offline"
              ? "Las impresiones y cobros no llegarán al servidor. Revisa el router o la WiFi."
              : quality.level === "slow"
              ? "El TPV puede tardar en responder. Si persiste, acerca el TPV al router."
              : quality.level === "fair"
              ? "La conexión es irregular — puede que notes lentitud al comandar."
              : "Conexión saludable."}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}

function Row({ label, value, color }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 600, color: color || "text.primary" }}>
        {value}
      </Typography>
    </Stack>
  );
}
